/**
 * Cookie / analytics consent.
 * Necessary cookies (session, language) are not gated here.
 *
 * Granular preferences (v2):
 * - analytics: GA4 / Plausible (+ Clarity on marketing paths only)
 * - sessionReplay: Microsoft Clarity on all paths, including CV surfaces
 *
 * Legacy `clarcv_cookie_consent` = accepted|rejected is still written for
 * older callers. Legacy "accepted" never implies sessionReplay.
 */

export const COOKIE_CONSENT_KEY = 'clarcv_cookie_consent';
export const COOKIE_PREFS_KEY = 'clarcv_cookie_prefs_v2';
export const COOKIE_CONSENT_EVENT = 'clarcv-cookie-consent';
export const COOKIE_BANNER_OPEN_EVENT = 'clarcv-cookie-banner-open';
export const COOKIE_CONSENT_REQUIRED_EVENT = 'clarcv-cookie-consent-required';
export const CONSENT_ACCEPTED = 'accepted';
export const CONSENT_REJECTED = 'rejected';

const isBrowser = () => typeof window !== 'undefined';

/**
 * @typedef {{ analytics: boolean, sessionReplay: boolean, v: 2 }} CookiePreferences
 */

/**
 * Parse stored v2 preferences, or derive from legacy consent.
 *
 * @returns {CookiePreferences|null} Null when the user has not chosen yet.
 */
export const getCookiePreferences = () => {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(COOKIE_PREFS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return {
          analytics: Boolean(parsed.analytics),
          sessionReplay: Boolean(parsed.sessionReplay),
          v: 2,
        };
      }
    }
  } catch (err) {
    // Fall through to legacy key.
  }

  const legacy = getCookieConsentLegacy();
  if (legacy === CONSENT_ACCEPTED) {
    return { analytics: true, sessionReplay: false, v: 2 };
  }
  if (legacy === CONSENT_REJECTED) {
    return { analytics: false, sessionReplay: false, v: 2 };
  }
  return null;
};

/**
 * Stored legacy analytics consent, or null if unset.
 *
 * @returns {'accepted'|'rejected'|null}
 */
const getCookieConsentLegacy = () => {
  if (!isBrowser()) return null;
  try {
    const value = window.localStorage.getItem(COOKIE_CONSENT_KEY);
    if (value === CONSENT_ACCEPTED || value === CONSENT_REJECTED) {
      return value;
    }
  } catch (err) {
    return null;
  }
  return null;
};

/**
 * Stored analytics consent, or null if the user has not chosen yet.
 * Derived from v2 prefs when present.
 *
 * @returns {'accepted'|'rejected'|null}
 */
export const getCookieConsent = () => {
  const prefs = getCookiePreferences();
  if (!prefs) {
    return getCookieConsentLegacy();
  }
  return prefs.analytics ? CONSENT_ACCEPTED : CONSENT_REJECTED;
};

/**
 * True when the user accepted non-essential analytics cookies (GA4 / Plausible).
 *
 * @returns {boolean}
 */
export const hasAnalyticsConsent = () => {
  const prefs = getCookiePreferences();
  if (prefs) return prefs.analytics === true;
  return getCookieConsentLegacy() === CONSENT_ACCEPTED;
};

/**
 * True when the user explicitly opted into full session replay (Clarity on
 * product paths, including screens where a CV may be visible).
 *
 * @returns {boolean}
 */
export const hasSessionReplayConsent = () => {
  const prefs = getCookiePreferences();
  return prefs?.sessionReplay === true;
};

/**
 * Persist granular preferences and notify listeners.
 * Reloads when analytics or sessionReplay is withdrawn so scripts unload.
 *
 * @param {{ analytics: boolean, sessionReplay?: boolean }} next
 */
export const setCookiePreferences = ({ analytics, sessionReplay = false }) => {
  if (!isBrowser()) return;

  const previous = getCookiePreferences();
  const prefs = {
    analytics: Boolean(analytics),
    // Session replay requires analytics (Clarity shares the same vendor load).
    sessionReplay: Boolean(analytics) && Boolean(sessionReplay),
    v: 2,
  };
  const legacy = prefs.analytics ? CONSENT_ACCEPTED : CONSENT_REJECTED;

  try {
    window.localStorage.setItem(COOKIE_PREFS_KEY, JSON.stringify(prefs));
    window.localStorage.setItem(COOKIE_CONSENT_KEY, legacy);
  } catch (err) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(COOKIE_CONSENT_EVENT, {
      detail: { ...prefs, value: legacy },
    })
  );

  const withdrewAnalytics = previous?.analytics === true && !prefs.analytics;
  const withdrewReplay = previous?.sessionReplay === true && !prefs.sessionReplay;
  if (withdrewAnalytics || withdrewReplay) {
    window.location.reload();
  }
};

/**
 * Persist legacy accept/reject. Accept keeps prior sessionReplay if already on.
 *
 * @param {'accepted'|'rejected'} value
 */
export const setCookieConsent = (value) => {
  if (!isBrowser() || (value !== CONSENT_ACCEPTED && value !== CONSENT_REJECTED)) {
    return;
  }
  if (value === CONSENT_REJECTED) {
    setCookiePreferences({ analytics: false, sessionReplay: false });
    return;
  }
  const previous = getCookiePreferences();
  setCookiePreferences({
    analytics: true,
    sessionReplay: previous?.sessionReplay === true,
  });
};

/**
 * Re-open the cookie banner from the footer (change of mind).
 *
 * @param {{ required?: boolean }} [opts] - When true, user must Accept or Reject.
 */
export const openCookieBanner = ({ required = false } = {}) => {
  if (!isBrowser()) return;
  window.dispatchEvent(
    new CustomEvent(COOKIE_BANNER_OPEN_EVENT, { detail: { required: Boolean(required) } })
  );
};

/**
 * Block until the user accepts or rejects analytics cookies.
 * Opens the banner in required mode when no choice exists yet.
 *
 * @returns {Promise<'accepted'|'rejected'>}
 */
export const requireCookieChoice = () =>
  new Promise((resolve) => {
    if (!isBrowser()) {
      resolve(CONSENT_REJECTED);
      return;
    }
    const existing = getCookieConsent();
    if (existing === CONSENT_ACCEPTED || existing === CONSENT_REJECTED) {
      resolve(existing);
      return;
    }
    const onChoice = (event) => {
      window.removeEventListener(COOKIE_CONSENT_EVENT, onChoice);
      const detail = event.detail;
      const accepted =
        typeof detail === 'object' && detail !== null
          ? Boolean(detail.analytics)
          : detail === CONSENT_ACCEPTED;
      resolve(accepted ? CONSENT_ACCEPTED : CONSENT_REJECTED);
    };
    window.addEventListener(COOKIE_CONSENT_EVENT, onChoice);
    openCookieBanner({ required: true });
  });

/**
 * Marketing / auth pages (GA4 pageviews; Clarity uses this plus from-file
 * when sessionReplay is off).
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export const isPublicAnalyticsPath = (pathname) => {
  const path = (pathname || '/').replace(/\/+$/, '') || '/';
  if (path === '/login') return true;

  const parts = path.split('/').filter(Boolean);
  if (parts.length === 0) return false;
  const lang = parts[0];
  if (lang !== 'es' && lang !== 'en') return false;
  if (parts.length === 1) return true;

  const rest = parts.slice(1).join('/');
  if (rest === 'pricing' || rest === 'privacy' || rest === 'terms' || rest === 'blog') {
    return true;
  }
  if (rest.startsWith('blog/')) return true;
  return false;
};
